import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { Transition } from '@headlessui/react';

import { useAikoSupport } from '@/components/composite/SparksAgent/hooks/useAikoSupport';
import { useToastsContext } from '@/utils/hooks/context/toast/useToastsContext';
import {
    createAiConversation,
    sendAiMessage,
    useAiConversationMessages,
    useAiConversations
} from '@/utils/hooks/swr/ai';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import {
    AI_CONVERSATION_MESSAGES_PAGE_SIZE,
    BOTTOM_GAP,
    CONTAINER_WIDTH,
    DEFAULT_HEIGHT,
    GAP_BELOW_NAVBAR,
    MIN_HEIGHT,
    STORAGE_KEY,
    STORAGE_KEY_OPEN,
    TOP_NAVBAR_HEIGHT
} from '@/components/composite/SparksAgent/const';

import {
    SparksAgentService,
    SparksConversation,
    SparksFAB,
    SparksFooter,
    SparksHeader,
    SparksInput,
    type SparksMessage,
    SparksWelcome
} from '@/components/composite/SparksAgent';

export function SparksAgent() {
    const { t } = useTranslation('sparks');
    const { toastError } = useToastsContext();
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
    const [containerHeight, setContainerHeight] = useState(DEFAULT_HEIGHT);
    const [isResizing, setIsResizing] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [typewriterMessageId, setTypewriterMessageId] = useState<string | null>(null);

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const mouseMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
    const mouseUpRef = useRef<(() => void) | null>(null);

    const handleMinimize = useCallback(() => {
        setTypewriterMessageId(null);
        setIsOpen(false);
        try {
            localStorage.setItem(STORAGE_KEY_OPEN, 'false');
        } catch {
            /* noop */
        }
    }, []);

    const { onNeedSupportClick, isAikoOpen } = useAikoSupport({ onBeforeOpen: handleMinimize });
    const hasResolvedInitialConversation = useRef(false);

    const {
        conversations,
        isLoading: isLoadingConversations,
        mutate: mutateConversations
    } = useAiConversations({ shouldFetch: isOpen });

    const {
        messages: apiMessages,
        isLoading: isLoadingMessages,
        mutate: mutateMessages,
        hasMore,
        loadMore,
        isLoadingMore
    } = useAiConversationMessages({
        conversationId: currentConversationId,
        shouldFetch: Boolean(currentConversationId)
    });

    const mutateMessagesRef = useRef(mutateMessages);
    mutateMessagesRef.current = mutateMessages;

    const messages: SparksMessage[] = apiMessages.map((message) =>
        SparksAgentService.toSparksMessage(message)
    );
    const conversationHasMessages = messages.length > 0;
    // Limit: container top stays below navbar; container bottom stays above viewport bottom gap
    const getMaxContainerHeight = useCallback(() => {
        return window.innerHeight - (TOP_NAVBAR_HEIGHT + GAP_BELOW_NAVBAR + BOTTOM_GAP);
    }, []);

    const handleOpen = useCallback(() => {
        setIsOpen(true);
        try {
            localStorage.setItem(STORAGE_KEY_OPEN, 'true');
        } catch {
            /* noop */
        }
    }, []);

    useEffect(() => {
        if (!isOpen || isLoadingConversations || hasResolvedInitialConversation.current) return;
        if (conversations.length > 0) {
            setCurrentConversationId(conversations[0].id);
        }

        hasResolvedInitialConversation.current = true;
    }, [isOpen, isLoadingConversations, conversations.length]);

    useEffect(() => {
        setTypewriterMessageId(null);
    }, [currentConversationId]);

    const handleTypewriterComplete = useCallback((messageId: string) => {
        setTypewriterMessageId((current) => (current === messageId ? null : current));
    }, []);

    const handleNewChat = useCallback(async () => {
        setInputValue('');
        setTypewriterMessageId(null);
        try {
            const response = await createAiConversation();
            setCurrentConversationId(response.id);
            void mutateMessages();
            void mutateConversations();
        } catch {
            toastError({ title: t('error_start_conversation') });
        }
    }, [mutateMessages, mutateConversations, t, toastError]);

    const getConversationId = useCallback(async () => {
        if (currentConversationId) return currentConversationId;

        try {
            const response = await createAiConversation();
            flushSync(() => setCurrentConversationId(response.id));
            return response.id;
        } catch {
            return null;
        }
    }, [currentConversationId]);

    const handleSend = useCallback(
        async (textOverride?: string, options?: { retryMessageId?: string }) => {
            const trimmed = (textOverride ?? inputValue ?? '').trim();

            const conversationId = await getConversationId();

            if (!trimmed || !conversationId || isSending) return;

            const optimisticMsg = SparksAgentService.createOptimisticUserMessage(trimmed);
            const shouldAppendOptimisticMessage = !options?.retryMessageId;

            if (textOverride === undefined) {
                setInputValue('');
            }
            setError(null);
            setIsSending(true);

            try {
                if (shouldAppendOptimisticMessage) {
                    await mutateMessagesRef.current(
                        (current) =>
                            SparksAgentService.appendOptimisticToInfiniteData({
                                pages: current,
                                message: optimisticMsg,
                                perPage: AI_CONVERSATION_MESSAGES_PAGE_SIZE
                            }),
                        { revalidate: false }
                    );
                }

                const { data: outputMessage } = await sendAiMessage(conversationId, {
                    input: trimmed,
                    context: []
                });

                await mutateMessagesRef.current(
                    (current) =>
                        SparksAgentService.appendAIOutputToInfiniteData({
                            pages: current,
                            serverMessage: outputMessage
                        }),
                    { revalidate: false }
                );

                setTypewriterMessageId(outputMessage.id);
            } catch {
                setError(t('error_something_wrong'));
                toastError({ title: t('error_send_message') });
            } finally {
                setIsSending(false);
            }
        },
        [inputValue, isSending, getConversationId, t, toastError]
    );

    const handleRetry = useCallback(() => {
        const pending = SparksAgentService.findNewestPendingOptimisticUserMessage(apiMessages);
        const retryText =
            pending?.role === 'user'
                ? SparksAgentService.getAiMessageText(pending).trim()
                : inputValue.trim();
        if (!retryText) return;
        setError(null);
        void handleSend(retryText, { retryMessageId: pending?.id });
    }, [apiMessages, handleSend, inputValue]);

    // Resize logic: drag top edge to adjust height (desktop only)
    const handleResizeMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setIsResizing(true);
            const startY = e.clientY;
            const startHeight = containerHeight;

            const handleMouseMove = (moveEvent: MouseEvent) => {
                const delta = startY - moveEvent.clientY;

                const newHeight = Math.min(
                    Math.max(startHeight + delta, MIN_HEIGHT),
                    getMaxContainerHeight()
                );
                setContainerHeight(newHeight);
            };

            const handleMouseUp = () => {
                setIsResizing(false);
                setContainerHeight((h) => {
                    try {
                        localStorage.setItem(STORAGE_KEY, String(h));
                    } catch {
                        /* noop */
                    }
                    return h;
                });
                mouseMoveRef.current = null;
                mouseUpRef.current = null;
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };

            mouseMoveRef.current = handleMouseMove;
            mouseUpRef.current = handleMouseUp;
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [containerHeight]
    );

    useEffect(() => {
        try {
            const storedOpen = localStorage.getItem(STORAGE_KEY_OPEN);
            if (storedOpen !== null) setIsOpen(storedOpen === 'true');
            const storedHeight = localStorage.getItem(STORAGE_KEY);
            if (storedHeight) {
                const parsed = parseFloat(storedHeight);

                if (!isNaN(parsed) && parsed >= MIN_HEIGHT)
                    setContainerHeight(Math.min(parsed, getMaxContainerHeight()));
            }
        } catch {
            /* localStorage unavailable */
        }
    }, []);

    useEffect(() => {
        return () => {
            if (mouseMoveRef.current) {
                document.removeEventListener('mousemove', mouseMoveRef.current);
            }
            if (mouseUpRef.current) {
                document.removeEventListener('mouseup', mouseUpRef.current);
            }
        };
    }, []);

    const renderContainerContent = () => (
        <>
            <SparksHeader onMinimize={handleMinimize} onNewChat={handleNewChat} />
            {conversationHasMessages ? (
                <SparksConversation
                    messages={messages}
                    isLoading={isLoadingMessages}
                    isSending={isSending}
                    hasMore={hasMore}
                    loadMore={loadMore}
                    isLoadingMore={isLoadingMore}
                    error={error}
                    onRetry={handleRetry}
                    typewriterMessageId={typewriterMessageId}
                    onTypewriterComplete={handleTypewriterComplete}
                />
            ) : (
                <SparksWelcome onActionClick={handleSend} />
            )}
            <SparksInput
                ref={inputRef}
                value={inputValue}
                onChange={setInputValue}
                onSend={handleSend}
                disabled={isSending || isLoadingConversations}
            />
            <SparksFooter onNeedSupportClick={onNeedSupportClick} />
        </>
    );

    return (
        <>
            {/* FAB toggle - visible when Agent is closed and Aiko is closed */}
            <div
                className={classNames('fixed bottom-4 right-4 z-50', {
                    'pointer-events-none opacity-0': isOpen || isAikoOpen
                })}>
                <SparksFAB onClick={handleOpen} />
            </div>

            {/* Desktop floating container */}
            <Transition
                show={isOpen}
                enter="transition-all duration-300 ease-out"
                enterFrom="opacity-0 translate-y-4 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="transition-all duration-200 ease-in"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-4 scale-95">
                <div
                    ref={containerRef}
                    className={classNames(
                        'fixed bottom-4 right-4 z-50',
                        'hidden min-h-0 flex-col overflow-hidden rounded-xl border border-icoGray-300 bg-white shadow-icoXS',
                        'dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack',
                        'md:flex'
                    )}
                    style={{
                        width: CONTAINER_WIDTH,
                        height: containerHeight
                    }}>
                    {/* Resize handle at top edge */}
                    <div
                        onMouseDown={handleResizeMouseDown}
                        className={classNames(
                            'absolute left-0 right-0 top-0 z-10 h-1.5 cursor-ns-resize',
                            'opacity-0 transition-opacity hover:opacity-100',
                            { 'opacity-100': isResizing }
                        )}
                    />
                    {renderContainerContent()}
                </div>
            </Transition>

            {/* Mobile container */}
            <Transition
                show={isOpen}
                enter="transition-all duration-300 ease-out"
                enterFrom="opacity-0 translate-y-full"
                enterTo="opacity-100 translate-y-0"
                leave="transition-all duration-200 ease-in"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-full">
                <div
                    className={classNames(
                        'fixed inset-x-3 inset-y-3 z-50 flex min-h-0 flex-col overflow-hidden rounded-xl border border-icoGray-300 bg-white shadow-icoXS',
                        'dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack',
                        'md:hidden'
                    )}>
                    {renderContainerContent()}
                </div>
            </Transition>
        </>
    );
}
